import { ComponentProps, MouseEventHandler, ReactElement, useState } from "react";
import { RelativeRoutingType, useLocation, useMatch } from "react-router-dom";
import { useCopyToClipboard } from "react-use";
import { DropdownMenuItemProps } from "@radix-ui/react-dropdown-menu";
import { cva, VariantProps } from "class-variance-authority";
import { formatRelativeDate } from "files/components/FileRow";
import { DeleteFileDialog } from "files/components/ui/delete-file-dialog";
import { FileActionDialog } from "files/components/ui/file-action-dialog";
import useBasePath from "files/hooks/useBasePath";
import useFileAccess from "files/hooks/useFileAccess";
import useFileTitle from "files/hooks/useFileTitle";
import { BASE_DIRECTORIES, encodeObjectKey, resolveRouterPath } from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import FileType from "files/types/enums/fileType";
import { JobType } from "files/types/interfaces/fileJob";
import { FileIcon, FolderIcon, MoreHorizontalIcon } from "lucide-react";
import prettyBytes from "pretty-bytes";
import { cn } from "~/lib/utils";
import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { FileDownload } from "./file-download";
import { ListItem, ListItemLinkProps } from "./list-card";
import { Skeleton } from "./skeleton";
import { TextOverflow } from "./text-overflow";
import useToast from "./toast";

const menuItemVariants = cva("px-4 py-1.5 rounded-none", {
  variants: {
    variant: {
      default: "hover:bg-primary hover:text-white",
      destructive: "text-destructive hover:text-white hover:bg-destructive",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const FileRowMenuItem = ({
  variant,
  className,
  onClick,
  ...props
}: DropdownMenuItemProps & VariantProps<typeof menuItemVariants>) => {
  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    onClick?.(event);
  };

  return (
    <DropdownMenuItem
      className={cn(menuItemVariants({ variant }), className)}
      onClick={handleClick}
      {...props}
    />
  );
};

const FileTitle = (props: { fileKey: string }) => {
  const { title, loading } = useFileTitle(props.fileKey);

  if (loading) {
    return (
      <Skeleton asChild className="w-[75px]">
        <span />
      </Skeleton>
    );
  }

  return <>{title}</>;
};

const FileMetadata = ({ className, children, ...props }: ComponentProps<"span">) => (
  <span className={cn("text-gray-900/70 text-nowrap font-sm leading-[1.43]", className)} {...props}>
    {children}
  </span>
);

const PendingActionLabel = ({ pendingAction }: { pendingAction: JobType }) => {
  const labels: Record<JobType, string> = {
    [JobType.MOVE]: "Moving",
    [JobType.RENAME]: "Renaming",
    [JobType.COPY]: "Copying",
  };

  return <span className="text-sm italic opacity-70 text-nowrap">{labels[pendingAction]}</span>;
};

interface UseLinkNavigationParams {
  fileKey: string;
  isFile: boolean;
  type?: FileType;
  size?: number;
}

function useLinkNavigation({ fileKey, isFile, type, size }: UseLinkNavigationParams) {
  const location = useLocation();
  const basePath = useBasePath();
  const { showSuccess } = useToast();
  const isGlobalFileContext = !useMatch("/workbenches/:workbenchId/files/*");
  const [, copy] = useCopyToClipboard();
  const routerPath = encodeObjectKey(resolveRouterPath(fileKey, isGlobalFileContext));

  const linkProps: ListItemLinkProps = isFile
    ? {
        // File preview stays in current place, updating
        // router state with file key and size information
        to: { pathname: ".", search: location.search },
        navOptions: {
          replace: true,
          relative: "path" as RelativeRoutingType,
          state: { preview: { key: fileKey, size } },
        },
      }
    : {
        to: routerPath,
        navOptions: {
          relative: (fileKey.startsWith("..") ? "path" : "route") as RelativeRoutingType,
        },
      };

  const handleCopyLink = () => {
    // Add preview qs for files (see `useFiles` hook for more info on loading patterns)
    const qs = type === FileType.FILE ? "?preview=1" : "";
    const url = new URL(`${basePath}/${routerPath}${qs}`, window.location.origin);
    showSuccess("Link copied to clipboard");
    copy(url.toString());
  };

  return { routerPath, linkProps, handleCopyLink };
}

interface OnRowClickParams {
  fileKey: string;
  currentPath: string | undefined;
  type: FileType | undefined;
  s3Uri: string | undefined;
}

interface FileRowProps {
  fileKey: string;
  currentPath?: string;
  navigation: "in-memory" | "url-based";
  selectedFileKey?: string;
  /** This will be called when the user clicks on this row. Should be used with `in-memory` navigation */
  onRowClick?: (params: OnRowClickParams) => void;
  hideActions?: boolean;
  hideAllActions?: boolean;
  disabled?: boolean;
  fileMetadata?: {
    type?: FileType;
    subtitle?: string;
    size?: number;
    updatedAt?: string;
    pendingAction?: JobType;
    badge?: ReactElement;
    s3Uri?: string;
  };
}

const FileRow = ({
  fileKey,
  selectedFileKey,
  currentPath,
  onRowClick,
  navigation,
  hideActions,
  hideAllActions,
  disabled,
  fileMetadata: { type, subtitle, size, updatedAt, pendingAction, badge, s3Uri } = {},
}: FileRowProps) => {
  // File variables
  const isFile = type === FileType.FILE;
  const sizeLabel = isFile && typeof size === "number" ? prettyBytes(size) : null;
  const canModify = useFileAccess(fileKey, "write");

  // Link variables
  const { routerPath, linkProps, handleCopyLink } = useLinkNavigation({
    fileKey,
    isFile,
    type,
    size,
  });

  // Dialog states
  const [fileAction, setFileAction] = useState<JobType | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Other variables
  const { showSuccess } = useToast();
  const [, copy] = useCopyToClipboard();
  const showActions =
    !pendingAction &&
    !hideActions && // Disable for back nav (..)
    !hideAllActions && // External control of hiding all actions
    !new RegExp(`^${BaseDirectory.PROJECTS}/\\d+/$`).test(fileKey) && // Disable for top of project directories
    (navigation === "in-memory" || (navigation === "url-based" && !BASE_DIRECTORIES[routerPath])); // Disable for top level virtual directories
  const icon = isFile ? (
    <FileIcon className="stroke-gray-950 opacity-60 size-[18px]" />
  ) : (
    <FolderIcon className="stroke-purple-300 fill-purple-300 size-[18px]" />
  );

  // Handlers
  const handleItemClick = () => {
    onRowClick?.({ currentPath, fileKey, type: type, s3Uri });
  };

  const handleCopyS3Uri = () => {
    if (s3Uri) {
      copy(s3Uri);
      showSuccess("Cloud path copied to clipboard");
    }
  };

  return (
    <>
      <ListItem
        type={navigation === "url-based" ? "link" : "default"}
        className={cn(
          "group/row",
          selectedFileKey &&
            (selectedFileKey === fileKey || selectedFileKey === s3Uri) &&
            "border border-primary/80 bg-primary/5"
        )}
        rowContentClassName={cn("h-[22px] !box-content flex items-center gap-8 text-sm w-full", {
          "h-9": subtitle,
        })}
        disabled={!!pendingAction || disabled}
        icon={icon}
        onClick={handleItemClick}
        {...linkProps}
      >
        <div className="flex flex-col grow min-w-0">
          <TextOverflow autoTooltip className="text-sm font-normal leading-[1.75]">
            <FileTitle fileKey={fileKey} />
          </TextOverflow>
          {subtitle && (
            <TextOverflow autoTooltip className="text-xs text-gray-600 font-normal leading-[1.57]">
              {subtitle}
            </TextOverflow>
          )}
        </div>

        <FileMetadata className={cn("min-w-[75px] text-end", hideAllActions && "ml-auto")}>
          {sizeLabel}
        </FileMetadata>
        <FileMetadata className={cn("min-w-[125px]", hideAllActions && "min-w-fit")}>
          {updatedAt && formatRelativeDate(updatedAt)}
        </FileMetadata>

        {!hideAllActions && (
          <div className="min-w-20">
            {pendingAction && <PendingActionLabel pendingAction={pendingAction} />}
            {showActions && (
              <div className="flex justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "size-fit p-1 opacity-0 transition-opacity",
                        "hover:bg-gray-300",
                        "group-hover/row:opacity-100 hover:opacity-100 data-[state=open]:opacity-100"
                      )}
                    >
                      <MoreHorizontalIcon className="size-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="px-0 py-2">
                    {isFile && (
                      <FileDownload fileKey={fileKey}>
                        <FileRowMenuItem>Download</FileRowMenuItem>
                      </FileDownload>
                    )}
                    {navigation === "url-based" && (
                      <FileRowMenuItem onClick={handleCopyLink}>Copy Link</FileRowMenuItem>
                    )}
                    {s3Uri && (
                      <FileRowMenuItem onClick={handleCopyS3Uri}>Copy Cloud Path</FileRowMenuItem>
                    )}
                    <FileRowMenuItem onClick={() => setFileAction(JobType.COPY)}>
                      Copy {type === FileType.DIRECTORY ? "Folder" : "File"}
                    </FileRowMenuItem>
                    {canModify && (
                      <FileRowMenuItem onClick={() => setFileAction(JobType.RENAME)}>
                        Rename...
                      </FileRowMenuItem>
                    )}
                    {canModify && (
                      <FileRowMenuItem onClick={() => setFileAction(JobType.MOVE)}>
                        Move...
                      </FileRowMenuItem>
                    )}
                    {canModify && (
                      <FileRowMenuItem
                        variant="destructive"
                        onClick={() => setDeleteModalOpen(true)}
                      >
                        Delete
                      </FileRowMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        )}

        {badge}
      </ListItem>

      {fileAction && (
        <FileActionDialog
          fileKey={fileKey}
          open={!!fileAction}
          action={fileAction}
          isDirectory={type === FileType.DIRECTORY}
          handleOpenChange={(open) => {
            if (!open) {
              setFileAction(null);
            }
          }}
        />
      )}

      <DeleteFileDialog
        fileKey={fileKey}
        open={deleteModalOpen}
        isDirectory={type === FileType.DIRECTORY}
        handleClose={() => setDeleteModalOpen(false)}
      />
    </>
  );
};

export { FileRow, type FileRowProps, type OnRowClickParams };
